/**
 * Carregamento dos dados de nível e revalidação periódica.
 *
 * Os arquivos só são baixados quando a camada é ligada pela primeira vez.
 * Depois disso, o gancho de atualização automática do loop revalida a cada
 * ciclo — o pipeline publica de hora em hora, então a maioria das
 * revalidações vai devolver o mesmo dado, e isso é barato.
 */
import { carregarDados } from './api';
import { frescorDe } from './frescor';
import { useRiverStore } from './store';

let emVoo: AbortController | null = null;
let jaCarregou = false;

export async function carregarRios(force = false): Promise<void> {
  const { situacao } = useRiverStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;

  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;

  // A revalidação periódica é silenciosa: "Baixando…" piscando no painel a
  // cada ciclo automático, para um dado que quase sempre não mudou, é ruído.
  if (!force) useRiverStore.getState().setSituacao({ estado: 'loading', mensagem: 'Baixando as cotas…', erro: null });

  try {
    const dados = await carregarDados(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Publicação igual à que já está na tela: não trocar `dados` poupa a
    // reconstrução das features e todos os `useMemo` que dependem dela.
    const atual = useRiverStore.getState().dados;
    if (dados.gerado !== null && dados.gerado === atual.gerado) {
      useRiverStore.getState().setSituacao({ estado: 'ready', mensagem: null, erro: null });
      return;
    }
    useRiverStore.getState().setDados(dados);
    useRiverStore.getState().setSituacao({
      estado: 'ready',
      mensagem:
        dados.comReferencia === 0
          ? 'Sem referência histórica nesta publicação — as estações aparecem sem cor.'
          : null,
      erro: null,
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useRiverStore.getState().setSituacao({
      estado: 'error',
      mensagem: null,
      erro: e instanceof Error ? e.message : String(e),
    });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só se a camada estiver em uso. */
export function refreshRios(): void {
  if (!jaCarregou || !useRiverStore.getState().visivel) return;
  void carregarRios(true);
}

export function startRiverSync(): () => void {
  if (useRiverStore.getState().visivel) void carregarRios();
  return useRiverStore.subscribe((a, b) => {
    if (a.visivel && !b.visivel) void carregarRios();
  });
}

/** Ponto amarelo no trilho quando o dado publicado está velho. */
export function useRiosDesatualizados(): boolean {
  const gerado = useRiverStore((s) => s.dados.gerado);
  const visivel = useRiverStore((s) => s.visivel);
  const erro = useRiverStore((s) => s.situacao.estado === 'error');
  if (!visivel) return false;
  const f = frescorDe(gerado);
  return erro || f === 'atrasado' || f === 'velho';
}

/** Só para os testes. */
export function resetRiverSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
}
